import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Trash2,
  CalendarDays,
  Languages,
} from "lucide-react";
import {
  API,
  REQ_STATUS_CLS,
} from "./shared";

export function ClinicsTab({ authHeader }) {
  const [clinics, setClinics] = useState([]);
  const [requests, setRequests] = useState([]);
  const [clientReqs, setClientReqs] = useState([]);
  const [reviewForms, setReviewForms] = useState({}); // reqId -> {price, note}
  const [presets, setPresets] = useState([]);
  const [presetsDirty, setPresetsDirty] = useState(false);
  const [codes, setCodes] = useState([]);
  const [codeEmail, setCodeEmail] = useState("");
  const [linkCopied, setLinkCopied] = useState("");
  const [interpreters, setInterpreters] = useState([]);
  const [photos, setPhotos] = useState({});
  const [interpForm, setInterpForm] = useState({ full_name: "", languages: ["ht"], bio: "" });
  const [savingInterp, setSavingInterp] = useState(false);

  const loadPhotos = async (list) => {
    const next = {};
    for (const i of list) {
      if (!i.has_photo) continue;
      try {
        const res = await axios.get(`${API}/interpreters/${i.id}/photo`, {
          headers: authHeader(), responseType: "blob",
        });
        next[i.id] = URL.createObjectURL(res.data);
      } catch {}
    }
    setPhotos(next);
  };

  const load = async () => {
    try {
      const [c, r, i, cr, tp, cc] = await Promise.all([
        axios.get(`${API}/admin/clinics`, { headers: authHeader() }),
        axios.get(`${API}/admin/interpreter-requests`, { headers: authHeader() }),
        axios.get(`${API}/admin/interpreters`, { headers: authHeader() }),
        axios.get(`${API}/admin/client-interpreter-requests`, { headers: authHeader() }),
        axios.get(`${API}/admin/travel-presets`, { headers: authHeader() }),
        axios.get(`${API}/admin/companion-codes`, { headers: authHeader() }),
      ]);
      setClinics(c.data || []);
      setRequests(r.data || []);
      setInterpreters(i.data || []);
      setClientReqs(cr.data || []);
      setPresets(tp.data.presets || []);
      setCodes(cc.data || []);
      loadPhotos(i.data || []);
    } catch {}
  };

  const createCode = async (e) => {
    e.preventDefault();
    if (!codeEmail.trim()) return;
    try {
      await axios.post(`${API}/admin/companion-codes`, { client_email: codeEmail.trim() }, { headers: authHeader() });
      setCodeEmail("");
      load();
    } catch {}
  };

  const revokeCode = async (code) => {
    try {
      await axios.post(`${API}/admin/companion-codes/${code}/revoke`, {}, { headers: authHeader() });
      load();
    } catch {}
  };

  const [codeTexted, setCodeTexted] = useState("");
  const [codeTextErr, setCodeTextErr] = useState("");
  const textCode = async (code) => {
    setCodeTextErr("");
    try {
      await axios.post(`${API}/admin/companion-codes/${code}/text`, {}, { headers: authHeader() });
      setCodeTexted(code);
      setTimeout(() => setCodeTexted(""), 4000);
    } catch (e) {
      setCodeTextErr(e?.response?.data?.detail || "Could not send the text.");
      setTimeout(() => setCodeTextErr(""), 5000);
    }
  };

  const copyAvailabilityLink = (i) => {
    const url = `${window.location.origin}/interpreter/${i.availability_token}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(i.id);
      setTimeout(() => setLinkCopied(""), 3000);
    });
  };

  const savePresets = async () => {
    try {
      const res = await axios.put(`${API}/admin/travel-presets`, { presets }, { headers: authHeader() });
      setPresets(res.data.presets);
      setPresetsDirty(false);
    } catch {}
  };

  const reviewClientReq = async (id, action) => {
    const f = reviewForms[id] || {};
    try {
      await axios.post(`${API}/admin/client-interpreter-requests/${id}/review`, {
        action,
        quoted_amount: f.price ? Number(f.price) : null,
        admin_note: f.note || "",
      }, { headers: authHeader() });
      load();
    } catch {}
  };

  const clientReqStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/client-interpreter-requests/${id}/status`, { status }, { headers: authHeader() });
      load();
    } catch {}
  };

  const addInterpreter = async (e) => {
    e.preventDefault();
    if (savingInterp) return;
    setSavingInterp(true);
    try {
      await axios.post(`${API}/admin/interpreters`, interpForm, { headers: authHeader() });
      setInterpForm({ full_name: "", languages: ["ht"], bio: "" });
      load();
    } catch {} finally {
      setSavingInterp(false);
    }
  };

  const removeInterpreter = async (id) => {
    try {
      await axios.delete(`${API}/admin/interpreters/${id}`, { headers: authHeader() });
      load();
    } catch {}
  };

  const uploadPhoto = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/admin/interpreters/${id}/photo`, fd, { headers: authHeader() });
      load();
    } catch {}
  };

  const assignInterpreter = async (reqId, interpreterId) => {
    if (!interpreterId) return;
    try {
      await axios.post(`${API}/admin/interpreter-requests/${reqId}/assign`,
        { interpreter_id: interpreterId }, { headers: authHeader() });
      load();
    } catch {}
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setClinicStatus = async (id, status) => {
    await axios.post(`${API}/admin/clinics/${id}/status`, { status }, { headers: authHeader() });
    load();
  };

  const setRequestStatus = async (id, status) => {
    const note = status === "confirmed" ? window.prompt("Note for the clinic (optional — e.g. interpreter name):") || "" : "";
    await axios.post(`${API}/admin/interpreter-requests/${id}/status`, { status, admin_note: note }, { headers: authHeader() });
    load();
  };

  const CLINIC_CLS = {
    pending: "bg-[#AE8F78]/15 text-[#875F45] border-[#AE8F78]/40",
    approved: "bg-[#3F5043]/10 text-[#3F5043] border-[#3F5043]/30",
    suspended: "bg-[#875F45]/10 text-[#875F45]/70 border-[#875F45]/20",
  };

  return (
    <div data-testid="clinics-tab" className="space-y-10">
      <div>
        <h3 className="font-serif text-2xl text-[#3F5043] mb-4">Clinic partner accounts</h3>
        <div data-testid="admin-clinics-list" className="space-y-3">
          {clinics.length === 0 && (
            <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-8 text-center text-sm text-[#3F5043]/60">
              No clinic accounts yet. Clinics sign up at /clinic.
            </div>
          )}
          {clinics.map((c, i) => (
            <div key={c.id} data-testid={`admin-clinic-${i}`}
              className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-serif text-lg text-[#3F5043]">{c.clinic_name}</div>
                <div className="text-xs text-[#3F5043]/60">
                  {c.contact_name} · {c.email}{c.phone ? ` · ${c.phone}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-[0.15em] font-semibold px-3 py-1.5 rounded-full border ${CLINIC_CLS[c.status] || CLINIC_CLS.pending}`}>
                  {c.status}
                </span>
                <span data-testid={`clinic-sub-${i}`} className={`text-[10px] uppercase tracking-[0.15em] font-semibold px-3 py-1.5 rounded-full border ${
                  c.subscription_status === "active"
                    ? "bg-[#899480]/15 text-[#3F5043] border-[#899480]/40"
                    : c.subscription_status === "grandfathered"
                      ? "bg-[#AE8F78]/15 text-[#875F45] border-[#AE8F78]/40"
                      : "bg-red-50 text-red-700 border-red-200"
                }`}>
                  {c.subscription_status === "active" ? "$199/mo" : c.subscription_status === "grandfathered" ? "legacy" : "no plan"}
                </span>
                {c.status !== "approved" && (
                  <button onClick={() => setClinicStatus(c.id, "approved")} data-testid={`clinic-approve-${i}`}
                    className="px-4 py-2 rounded-full bg-[#3F5043] text-[#F8F7F3] text-xs font-medium hover:bg-[#875F45] transition-colors">
                    Approve
                  </button>
                )}
                {c.status === "approved" && (
                  <button onClick={() => setClinicStatus(c.id, "suspended")} data-testid={`clinic-suspend-${i}`}
                    className="px-4 py-2 rounded-full bg-[#EDEDEE] text-[#3F5043] text-xs font-medium hover:bg-[#875F45] hover:text-[#F8F7F3] transition-colors">
                    Suspend
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-serif text-2xl text-[#3F5043] mb-4">Interpreters</h3>
        <form onSubmit={addInterpreter} data-testid="interpreter-form"
          className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input
            data-testid="interp-name"
            required
            placeholder="Full name"
            value={interpForm.full_name}
            onChange={(e) => setInterpForm({ ...interpForm, full_name: e.target.value })}
            className="bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-2.5 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]"
          />
          <div className="flex items-center gap-4 text-sm text-[#3F5043]">
            <label className="flex items-center gap-2">
              <input type="checkbox" data-testid="interp-lang-ht" className="accent-[#3F5043]"
                checked={interpForm.languages.includes("ht")}
                onChange={(e) => setInterpForm({
                  ...interpForm,
                  languages: e.target.checked
                    ? [...new Set([...interpForm.languages, "ht"])]
                    : interpForm.languages.filter((l) => l !== "ht"),
                })} />
              Haitian Creole
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" data-testid="interp-lang-fr" className="accent-[#3F5043]"
                checked={interpForm.languages.includes("fr")}
                onChange={(e) => setInterpForm({
                  ...interpForm,
                  languages: e.target.checked
                    ? [...new Set([...interpForm.languages, "fr"])]
                    : interpForm.languages.filter((l) => l !== "fr"),
                })} />
              French
            </label>
          </div>
          <div className="flex gap-3">
            <input
              data-testid="interp-bio"
              placeholder="Short bio (optional)"
              value={interpForm.bio}
              onChange={(e) => setInterpForm({ ...interpForm, bio: e.target.value })}
              className="flex-1 bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-2.5 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]"
            />
            <button type="submit" disabled={savingInterp} data-testid="interp-add"
              className="rounded-full px-5 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors disabled:opacity-40">
              Add
            </button>
          </div>
        </form>
        <div data-testid="interpreters-list" className="space-y-3">
          {interpreters.length === 0 && (
            <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-6 text-center text-sm text-[#3F5043]/60">
              No interpreters yet — add your first one above.
            </div>
          )}
          {interpreters.map((p, i) => (
            <div key={p.id} data-testid={`interpreter-row-${i}`}
              className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-4 flex items-center gap-4">
              {photos[p.id] ? (
                <img src={photos[p.id]} alt={p.full_name}
                  className="w-12 h-12 rounded-full object-cover border border-[#BCB3A6]/40" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#3F5043] text-[#AE8F78] flex items-center justify-center font-serif text-lg">
                  {p.full_name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg text-[#3F5043]">{p.full_name}</div>
                <div className="text-xs text-[#3F5043]/60">
                  {p.languages.map((l) => (l === "ht" ? "Haitian Creole" : "French")).join(" · ")}
                  {p.bio ? ` — ${p.bio}` : ""}
                </div>
                <div className="text-[11px] text-[#899480] mt-0.5" data-testid={`interp-avail-${i}`}>
                  {(() => {
                    const names = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                    const on = Object.entries(p.availability?.weekly || {}).filter(([, v]) => v).map(([d]) => names[Number(d)]);
                    return on.length
                      ? `Available ${on.join(" ")}${p.availability?.blocked_dates?.length ? ` · ${p.availability.blocked_dates.length} blocked date(s)` : ""}`
                      : "No availability set yet";
                  })()}
                </div>
              </div>
              <button
                data-testid={`interp-link-${i}`}
                onClick={() => copyAvailabilityLink(p)}
                className="text-xs text-[#3F5043] hover:text-[#875F45] whitespace-nowrap inline-flex items-center gap-1 border border-[#BCB3A6]/50 rounded-full px-3 py-1.5"
              >
                {linkCopied === p.id ? "Link copied!" : "Copy availability link"}
              </button>
              <label data-testid={`interp-photo-${i}`}
                className="text-xs text-[#875F45] hover:text-[#3F5043] cursor-pointer whitespace-nowrap">
                {p.has_photo ? "Change photo" : "Add photo"}
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => uploadPhoto(p.id, e.target.files?.[0])} />
              </label>
              <button onClick={() => removeInterpreter(p.id)} data-testid={`interp-delete-${i}`}
                className="text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1">
                <Trash2 size={13} /> Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-serif text-2xl text-[#3F5043] mb-4">Interpreter requests</h3>
        <div data-testid="admin-interpreter-requests" className="space-y-3">
          {requests.length === 0 && (
            <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-8 text-center text-sm text-[#3F5043]/60">
              No interpreter requests yet.
            </div>
          )}
          {requests.map((r, i) => (
            <div key={r.id} data-testid={`admin-request-${i}`}
              className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-serif text-lg text-[#3F5043]">
                    {r.clinic_name} — {r.appointment_type}
                  </div>
                  <div className="text-xs text-[#3F5043]/60 flex flex-wrap gap-x-3 gap-y-1 mt-1">
                    <span className="inline-flex items-center gap-1">
                      <Languages size={12} /> {r.language === "ht" ? "Haitian Creole" : "French"}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <CalendarDays size={12} /> {r.appointment_date} · {r.appointment_time}
                    </span>
                    <span>{r.mode === "virtual" ? "Virtual" : `In person — ${r.location}`}</span>
                    <span>Patient ref: {r.patient_ref}</span>
                  </div>
                  {r.notes && <div className="text-xs text-[#3F5043]/70 mt-2 max-w-xl">{r.notes}</div>}
                  {r.admin_note && (
                    <div className="mt-2 text-xs bg-[#3F5043]/5 text-[#3F5043] rounded-xl px-3 py-2 inline-block">
                      Your note: {r.admin_note}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    {r.interpreter_name && (
                      <span data-testid={`req-interpreter-${i}`}
                        className="text-[11px] bg-[#899480]/15 text-[#3F5043] rounded-full px-3 py-1 font-medium">
                        {r.interpreter_name}
                      </span>
                    )}
                    <select
                      data-testid={`req-assign-${i}`}
                      value=""
                      onChange={(e) => assignInterpreter(r.id, e.target.value)}
                      className="text-xs bg-white border border-[#BCB3A6]/50 rounded-full px-3 py-1.5 text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]"
                    >
                      <option value="">{r.interpreter_name ? "Reassign…" : "Assign interpreter…"}</option>
                      {interpreters
                        .filter((p) => p.active && p.languages.includes(r.language))
                        .map((p) => (
                          <option key={p.id} value={p.id}>{p.full_name}</option>
                        ))}
                    </select>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`text-[10px] uppercase tracking-[0.15em] font-semibold px-3 py-1.5 rounded-full border ${REQ_STATUS_CLS[r.status] || REQ_STATUS_CLS.pending}`}>
                    {r.status}
                  </span>
                  <div className="flex gap-2">
                    {r.status === "pending" && (
                      <button onClick={() => setRequestStatus(r.id, "confirmed")} data-testid={`req-confirm-${i}`}
                        className="px-3 py-1.5 rounded-full bg-[#3F5043] text-[#F8F7F3] text-xs hover:bg-[#875F45] transition-colors">
                        Confirm
                      </button>
                    )}
                    {r.status === "confirmed" && (
                      <button onClick={() => setRequestStatus(r.id, "completed")} data-testid={`req-complete-${i}`}
                        className="px-3 py-1.5 rounded-full bg-[#899480] text-[#F8F7F3] text-xs hover:bg-[#3F5043] transition-colors">
                        Complete
                      </button>
                    )}
                    {(r.status === "pending" || r.status === "confirmed") && (
                      <button onClick={() => setRequestStatus(r.id, "canceled")} data-testid={`req-cancel-${i}`}
                        className="px-3 py-1.5 rounded-full bg-[#EDEDEE] text-[#875F45] text-xs hover:bg-[#875F45] hover:text-[#F8F7F3] transition-colors">
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-serif text-2xl text-[#3F5043] mb-1">Client interpreter requests</h3>
        <p className="text-xs text-[#3F5043]/60 mb-4">
          Patients request in-person interpreters from their portal ($80 Orlando base — adjust the price for
          locations farther out before accepting). They pay after you accept; payment confirms the request.
        </p>

        {/* Travel price presets — one-tap quoting */}
        <div className="bg-white border border-[#BCB3A6]/40 rounded-2xl p-4 mb-4" data-testid="travel-presets-editor">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#875F45]">Price presets</span>
            {presets.map((p, pi) => (
              <span key={pi} className="inline-flex items-center gap-1.5 bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-full px-3 py-1.5 text-xs text-[#3F5043]">
                <input
                  data-testid={`preset-label-${pi}`}
                  value={p.label}
                  onChange={(e) => { const n = [...presets]; n[pi] = { ...n[pi], label: e.target.value }; setPresets(n); setPresetsDirty(true); }}
                  className="w-24 bg-transparent focus:outline-none"
                />
                <span className="text-[#BCB3A6]">$</span>
                <input
                  data-testid={`preset-price-${pi}`}
                  type="number" min="10" max="2000" step="5"
                  value={p.price}
                  onChange={(e) => { const n = [...presets]; n[pi] = { ...n[pi], price: e.target.value }; setPresets(n); setPresetsDirty(true); }}
                  className="w-14 bg-transparent focus:outline-none"
                />
                <button
                  data-testid={`preset-del-${pi}`}
                  onClick={() => { setPresets(presets.filter((_, x) => x !== pi)); setPresetsDirty(true); }}
                  className="text-[#875F45] hover:text-red-700"
                >
                  <Trash2 size={12} />
                </button>
              </span>
            ))}
            <button
              data-testid="preset-add"
              onClick={() => { setPresets([...presets, { label: "New zone", price: 100 }]); setPresetsDirty(true); }}
              className="text-xs text-[#3F5043] border border-dashed border-[#BCB3A6] rounded-full px-3 py-1.5 hover:border-[#899480]"
            >
              + Add preset
            </button>
            {presetsDirty && (
              <button
                data-testid="preset-save"
                onClick={savePresets}
                className="text-xs rounded-full px-4 py-1.5 bg-[#3F5043] text-[#F8F7F3] font-medium hover:bg-[#875F45] transition-colors"
              >
                Save presets
              </button>
            )}
          </div>
          <p className="text-[11px] text-[#3F5043]/50">These appear as one-tap price chips when you review a request.</p>
        </div>

        {/* IVF support codes — $20/day admin fee instead of $80 + travel */}
        <div className="bg-white border border-[#BCB3A6]/40 rounded-2xl p-4 mb-4" data-testid="companion-codes">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#875F45] mb-2">
            IVF support codes ($20/day admin fee)
          </div>
          <form onSubmit={createCode} className="flex flex-wrap gap-2 mb-3">
            <input
              data-testid="code-email"
              type="email" required
              placeholder="Client email (must have paid IVF package)"
              value={codeEmail}
              onChange={(e) => setCodeEmail(e.target.value)}
              className="flex-1 min-w-[220px] bg-white border border-[#BCB3A6]/50 rounded-xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]"
            />
            <button type="submit" data-testid="code-generate"
              className="rounded-full px-4 py-2 bg-[#3F5043] text-[#F8F7F3] text-xs font-medium hover:bg-[#875F45] transition-colors">
              Generate code
            </button>
          </form>
          {codeTextErr && <p className="text-xs text-red-700 mb-2" data-testid="code-text-error">{codeTextErr}</p>}
          {codes.length > 0 && (
            <div className="space-y-1.5">
              {codes.map((c, ci) => (
                <div key={c.code} data-testid={`code-row-${ci}`}
                  className="flex flex-wrap items-center gap-2 text-xs bg-[#F8F7F3] rounded-xl px-3 py-2">
                  <span className="font-mono font-semibold text-[#3F5043]">{c.code}</span>
                  <span className="text-[#3F5043]/60">{c.client_email}</span>
                  {!c.ivf_paid && (
                    <span className="text-[#875F45] italic">no IVF payment on record — verify before sharing</span>
                  )}
                  <span className={`ml-auto font-semibold ${c.active ? "text-[#899480]" : "text-red-700"}`}>
                    {c.active ? "Active" : "Revoked"}
                  </span>
                  {c.active && (
                    <button onClick={() => textCode(c.code)} data-testid={`code-text-${ci}`}
                      className="text-[#3F5043] hover:text-[#875F45] font-medium">
                      {codeTexted === c.code ? "Texted ✓" : "Text code"}
                    </button>
                  )}
                  {c.active && (
                    <button onClick={() => revokeCode(c.code)} data-testid={`code-revoke-${ci}`}
                      className="text-red-700 hover:underline">Revoke</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div data-testid="client-interp-requests" className="space-y-3">
          {clientReqs.length === 0 && (
            <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-8 text-center text-sm text-[#3F5043]/60">
              No client interpreter requests yet.
            </div>
          )}
          {clientReqs.map((r, i) => {
            const f = reviewForms[r.id] || {};
            const setF = (patch) => setReviewForms({ ...reviewForms, [r.id]: { ...f, ...patch } });
            const pill =
              r.status === "confirmed" ? "bg-[#899480]/20 text-[#3F5043]"
              : r.status === "awaiting_payment" ? "bg-[#D4AF37]/20 text-[#875F45]"
              : r.status === "pending" ? "bg-[#AE8F78]/15 text-[#875F45]"
              : r.status === "completed" ? "bg-[#3F5043]/10 text-[#3F5043]"
              : "bg-red-100 text-red-700";
            return (
              <div key={r.id} data-testid={`cir-row-${i}`}
                className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-lg text-[#3F5043]">
                      {r.client_name || r.client_email}
                      <span className="mx-2 text-[#BCB3A6]">·</span>
                      <span className="text-base">{r.request_type === "attend" ? "Attend With Me" : (r.language === "ht" ? "Haitian Creole" : "French")}</span>
                      {r.code_applied && (
                        <span data-testid={`cir-code-badge-${i}`}
                          className="ml-2 text-[10px] uppercase tracking-wider font-sans font-bold bg-[#899480]/20 text-[#3F5043] rounded-full px-2.5 py-1 align-middle">
                          IVF code · $20/day
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#3F5043]/70 mt-1">
                      {r.date} at {r.time} · {r.clinic_name} · {r.address}
                    </div>
                    <div className="text-xs text-[#3F5043]/50 mt-0.5">
                      {r.client_email}{r.phone ? ` · ${r.phone}` : ""}
                      {r.notes ? ` — "${r.notes}"` : ""}
                    </div>
                    {r.admin_note && (
                      <div className="text-xs text-[#875F45] mt-1">Your note: {r.admin_note}</div>
                    )}
                    {r.status === "pending" && r.availability_hint && (
                      <div className="text-[11px] mt-1.5" data-testid={`cir-hint-${i}`}>
                        {r.availability_hint.available.length > 0 && (
                          <span className="text-[#899480]">
                            Available that day: {r.availability_hint.available.join(", ")}
                          </span>
                        )}
                        {r.availability_hint.unavailable.length > 0 && (
                          <span className="text-red-700 ml-1">
                            {r.availability_hint.available.length > 0 ? "· " : ""}
                            Not available: {r.availability_hint.unavailable.join(", ")}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span data-testid={`cir-status-${i}`}
                    className={`text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 rounded-full ${pill}`}>
                    {r.status === "awaiting_payment" ? `awaiting $${(r.quoted_amount / 100).toFixed(0)}` : r.status}
                  </span>
                </div>

                {r.status === "pending" && (
                  <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[#BCB3A6]/20 pt-4">
                    {!r.code_applied && presets.map((p, pi) => (
                      <button
                        key={pi}
                        data-testid={`cir-preset-${i}-${pi}`}
                        onClick={() => setF({ price: String(p.price) })}
                        className="text-[11px] rounded-full px-3 py-1.5 border border-[#AE8F78]/40 text-[#875F45] hover:bg-[#AE8F78]/10 transition-colors"
                      >
                        {p.label} · ${p.price}
                      </button>
                    ))}
                    {r.code_applied && (
                      <span className="text-[11px] text-[#899480] font-semibold">
                        IVF support code — flat $20 daily admin fee (no travel surcharge)
                      </span>
                    )}
                    <label className="text-xs text-[#3F5043]/70 flex items-center gap-2" style={r.code_applied ? { display: "none" } : {}}>
                      Price $
                      <input
                        data-testid={`cir-price-${i}`}
                        type="number" min="10" max="2000" step="5"
                        placeholder="80"
                        value={f.price ?? ""}
                        onChange={(e) => setF({ price: e.target.value })}
                        className="w-24 bg-white border border-[#BCB3A6]/50 rounded-xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]"
                      />
                    </label>
                    <input
                      data-testid={`cir-note-${i}`}
                      placeholder="Note to client (optional — e.g., travel fee)"
                      value={f.note || ""}
                      onChange={(e) => setF({ note: e.target.value })}
                      className="flex-1 min-w-[200px] bg-white border border-[#BCB3A6]/50 rounded-xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]"
                    />
                    <button onClick={() => reviewClientReq(r.id, "accept")} data-testid={`cir-accept-${i}`}
                      className="px-4 py-2 rounded-full bg-[#3F5043] text-[#F8F7F3] text-xs font-medium hover:bg-[#875F45] transition-colors">
                      {r.code_applied ? "Accept — $20 (IVF code)" : `Accept${f.price ? ` — $${f.price}` : " — $80"}`}
                    </button>
                    <button onClick={() => reviewClientReq(r.id, "decline")} data-testid={`cir-decline-${i}`}
                      className="px-4 py-2 rounded-full bg-[#EDEDEE] text-[#875F45] text-xs hover:bg-red-100 transition-colors">
                      Decline
                    </button>
                  </div>
                )}
                {r.status === "confirmed" && (
                  <div className="mt-3 flex gap-2 border-t border-[#BCB3A6]/20 pt-3">
                    <button onClick={() => clientReqStatus(r.id, "completed")} data-testid={`cir-complete-${i}`}
                      className="px-3 py-1.5 rounded-full bg-[#899480] text-white text-xs hover:bg-[#3F5043] transition-colors">
                      Complete
                    </button>
                    <button onClick={() => clientReqStatus(r.id, "canceled")} data-testid={`cir-cancel-${i}`}
                      className="px-3 py-1.5 rounded-full bg-[#EDEDEE] text-[#875F45] text-xs hover:bg-red-100 transition-colors">
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
